<div *ngIf="!isB2cPractice && !hasConsent">
  <app-proctoring-consent (consentResult)="handleConsent($event)"></app-proctoring-consent>
</div>

<div *ngIf="!isB2cPractice && hasConsent && !hasScreenShared">
  <app-screen-share-required 
    (screenShared)="handleScreenShare($event)" 
    (goBackEvent)="handleScreenShareBack()">
  </app-screen-share-required>
</div>

<ng-container *ngIf="isB2cPractice || (hasConsent && hasScreenShared)">
  <div *ngIf="isAnalyzing; else interviewPage" class="analyzing-container">
    <app-loader></app-loader>
    <h2>Analyzing Your Interview...</h2>
    <p>Our AI is processing your responses. This may take a few moments.</p>

    <div class="analyzing-steps">
      <div class="step"><i class="bi bi-cpu"></i> Analyzing Voice Tone</div>
      <div class="step"><i class="bi bi-chat-text"></i> Evaluating Answers</div>
      <div class="step"><i class="bi bi-person-badge"></i> Checking Confidence</div>
      <div class="step"><i class="bi bi-graph-up"></i> Preparing Report</div>
    </div>

    <div class="progress-loader"></div>
    <p class="progress-text">Please wait while we prepare your report...</p>
  </div>
</ng-container>

<ng-template #interviewPage>
  <app-proctoring-warning *ngIf="!isB2cPractice"></app-proctoring-warning>

  <div class="interview-page-dark">
    <!-- ===== TOP BAR ===== -->
    <div class="top-bar-dark">
      <div class="left">
        <div class="live-badge">
          <span class="live-dot"></span>
          LIVE
        </div>
        <ng-container *ngIf="logoUrl; else textOrg">
          <img [src]="logoUrl" alt="Company Logo" style="max-height: 24px; max-width: 100px; object-fit: contain; margin-right: 8px;">
        </ng-container>
        <ng-template #textOrg>
          <div class="org-name-v4">{{ organizationName }}</div>
        </ng-template>
        <span class="title">{{ sessionService.value.job_title || 'Software Engineer' }}</span>
      </div>

      <div class="center">
        <div class="duration-box">
          {{ timeRemaining }}
        </div>
      </div>

      <div class="right">
        <!-- Network Indicator (conditionally shown or integrated) -->
        <div class="network-badge" [ngClass]="networkQuality.toLowerCase()" *ngIf="networkQuality !== 'CHECKING'">
          <div class="signal-bars">
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
          </div>
        </div>

        <button class="end-btn" (click)="exitInterview()">
          <i class="bi bi-telephone-x-fill"></i> End Interview
        </button>
      </div>
    </div>

    <!-- ===== MAIN CONTENT ===== -->
    <div class="main-content-dark">
      <!-- LEFT SECTION (Video) -->
      <div class="left-section">
        <div class="video-wrapper">
          <video #videoElement autoplay muted playsinline class="main-video"></video>
          
          <!-- AI Interviewer Pill (Top Right) -->
          <div class="ai-pill">
            <div class="ai-icon">
              <i class="bi bi-stars"></i>
            </div>
            <div class="ai-info">
              <span class="name">{{ sessionService.value.interviewer_name || 'Aria' }}</span>
              <span class="role">AI Interviewer</span>
            </div>
          </div>

          <!-- User Pill (Bottom Left) -->
          <div class="user-pill">
            You
          </div>

          <!-- Controls Pill (Bottom Right) -->
          <div class="controls-pill">
            <span class="status-item"><i class="bi bi-mic-fill"></i> Mic on</span>
            <span class="status-item"><i class="bi bi-camera-video-fill"></i> Camera on</span>
          </div>
        </div>
      </div>

      <!-- RIGHT SECTION (Conversation) -->
      <div class="right-section">
        <div class="chat-header">
          <h4>Conversation</h4>
          <p>Live transcript of the interview</p>
        </div>

        <div class="chat-window" #chatWindow *ngIf="transcript.length; else waiting">
          <div class="chat-message" *ngFor="let msg of transcript; trackBy: trackByIndex"
            [ngClass]="{'user': msg.includes('🧑'), 'ai': msg.includes('🤖')}">
            
            <div class="message-label" *ngIf="msg.includes('🤖')">{{ sessionService.value.interviewer_name || 'ARIA' | uppercase }}</div>
            <div class="message-label" *ngIf="msg.includes('🧑')">YOU</div>
            
            <div class="message-body">
              {{ msg.replace('🧑 You: ', '').replace('🤖 AI: ', '') }}
            </div>
          </div>
        </div>

        <ng-template #waiting>
          <div class="waiting-state">
            <p>Waiting for conversation to begin...</p>
          </div>
        </ng-template>

        <!-- Bottom Listening Bar -->
        <div class="listening-bar">
          <div class="mic-icon-wrapper">
            <i class="bi bi-mic-fill"></i>
          </div>
          <div class="listening-info">
            <span>Listening to you...</span>
            <div class="waveform">
              <div class="bar"></div><div class="bar"></div><div class="bar"></div>
              <div class="bar"></div><div class="bar"></div><div class="bar"></div>
            </div>
          </div>
        </div>
        <video #snapshotVideo [style.display]="'none'" autoplay muted playsinline></video>
      </div>
    </div>
  </div>
</ng-template>

<!-- ===== LEAVE CONFIRMATION MODAL ===== -->
<div class="leave-overlay" *ngIf="showLeaveDialog" (click)="dismissLeaveDialog()">
  <div class="leave-modal" (click)="$event.stopPropagation()">
    <div class="leave-icon">
      <i class="bi bi-exclamation-triangle-fill"></i>
    </div>
    <h3>Leave Interview?</h3>
    <p>
      If you leave now, your interview will be <strong>automatically submitted</strong>
      with the responses recorded so far. This action cannot be undone.
    </p>
    <div class="leave-actions">
      <button class="stay-btn" (click)="dismissLeaveDialog()">
        <i class="bi bi-shield-check"></i> Stay in Interview
      </button>
      <button class="leave-btn" (click)="confirmLeave()">
        <i class="bi bi-box-arrow-right"></i> Leave & Submit
      </button>
    </div>
  </div>
</div>

<!-- Always-on audio nodes for WebRTC/Proctoring -->
<audio #remoteAudio autoplay></audio>